import React from 'react';
import { makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { categoryRailStore } from '@services/CategoryRailStore';
import SharedCategoryRail from '@components/SharedCategoryRail';
import Toast from '@components/Toast';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'highlight.js/styles/monokai-sublime.css';
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation
} from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';

import { Modals } from '@components/Modal';
import NavBar from '@components/NavBar';

import SharedNote from '@pages/SharedNote';
import SnapshotNote from '@pages/SnapshotNote';

import { FailedUi } from '@components/FailedUi';
import Dashboard from '@pages/Dashboard';
import Notes from '@pages/Notes';
import SearchPage from '@pages/SearchPage';
import Page404 from '@pages/Page404';
import '@services/ThemeService';

import './scss/app.scss';
import './scss/modal.scss';
import './scss/toast.scss';

// Matches server userService.toUiUser() projection
type UserData = { id: string; name?: string; familyName?: string; givenName?: string; picture?: string; email: string; emailMd5: string; role: string };
declare global {
  interface Window {
    user?: UserData;
  }
}

/**
 * Renders the top nav bar (hidden on standalone routes), the persistent category
 * rail, and the app routes. The rail sits in `.app-body` above <Routes> so it
 * stays mounted across Dashboard and Notes — no remount or re-sort.
 */
const AppShell = observer(function AppShell(): JSX.Element {
  const location = useLocation();
  const isStandalone = location.pathname.startsWith('/s/') || location.pathname.startsWith('/snapshot/');
  const store = categoryRailStore;

  const bodyClass = `app-body` +
    (store.isRailHidden ? ' rail-hidden' : '') +
    (store.mode === 'dashboard' && store.isMobileRailOpen ? ' rail-open' : '');

  return (
    <React.Suspense fallback={<div>Loading...</div>}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        {!isStandalone && <NavBar />}
        <main style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div className={bodyClass}>
            {!isStandalone && <SharedCategoryRail />}
            <Routes>
              <Route path="/s/*" element={<SharedNote />} />
              <Route path="/snapshot/:noteId/:snapshotId" element={<SnapshotNote />} />
              <Route path="/" element={<Dashboard />} />
              <Route path="/notes" element={<Notes />} />
              <Route path="/n/*" element={<Notes />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="*" element={<Page404 />} />
            </Routes>
          </div>
        </main>
      </div>
    </React.Suspense>
  );
});

@observer
class App extends React.Component {
  @observable UserData = window.user;
  @observable isLoading = true;

  constructor(props: {}) {
    super(props);
    makeObservable(this);
  }

  componentDidMount(): void {
    // Hide the HTML loading screen now that React has mounted
    const loader = document.querySelector('.app-splash');
    if (loader) {
      loader.remove();
    }
    void this._checkUserLoggedIn();
  }

  render(){

    if (this.isLoading) {
      return null;
    }

    return (
      <ErrorBoundary FallbackComponent={FailedUi}>
        <Toast />
        <BrowserRouter>
          <Modals />
          <ErrorBoundary FallbackComponent={FailedUi}>
            <AppShell />
          </ErrorBoundary>
        </BrowserRouter>
      </ErrorBoundary>
    );
  }

  private async _checkUserLoggedIn() {
    let isLoggedIn = !!window.user;
    if (!isLoggedIn) {
      const userDataResponse = await fetch('/auth/userinfo');
      if (userDataResponse.ok) {
        const userData: { isAuthenticated: boolean; user?: UserData; hasRefreshToken?: boolean } = await userDataResponse.json();
        if (userData.isAuthenticated && userData.user) {
          window.user = userData.user;
          this.UserData = userData.user;
          isLoggedIn = true;
        }

        if (!isLoggedIn && userData.hasRefreshToken) {
          await fetch('/auth/refresh', { method: 'POST', credentials: 'include' });
          const recheckResponse = await fetch('/auth/userinfo');
          if (recheckResponse.ok) {
            const recheckData: { isAuthenticated: boolean; user?: UserData } = await recheckResponse.json();
            if (recheckData.isAuthenticated && recheckData.user) {
              window.user = recheckData.user;
              this.UserData = recheckData.user;
              isLoggedIn = true;
            }
          }
        }
      }

      if (!isLoggedIn && !window.location.pathname.startsWith('/s/')){
        void this._redirectToLogin();
      }
    }
    runInAction(() => {
      this.isLoading = false;
    });
  }

  private async _redirectToLogin(){
    //show error message by throwing an unhandled rejection
    setTimeout(() => {
      throw new Error('User not authenticated')
    }, 1);
    // add 1s delay before redirect
    await new Promise(resolve => setTimeout(resolve, 1000));
    window.location.href = '/auth/login';
  }
};

export default App;